import React from "react";
import Head from "next/head";
import Link from "next/link";
import Navigation from "@/layout/navigation";
import WhoWeServeFeatureSection from "@/components/who-we-serve/WhoWeServeFeatureSection";

export default function ForHealthCoachesPageFr() {
  return (
    <>
      <Head>
        <title>
          Featcal pour Coach Santé | Plus d’adhérence, moins de relances
        </title>
        <meta
          name="description"
          content="Featcal aide les coachs santé à garder les clients réguliers entre check-ins : suivi structuré, plans dans l’app, et revue plus rapide pour coacher les résultats."
        />
      </Head>

      <header className="bg-gradient-to-b from-white from-20% to-blue-300 flex flex-col gap-16 items-center justify-between overflow-hidden px-8 xl:px-0 z-[51] relative pb-20">
        <Navigation />
        <div className="flex flex-col md:items-center md:justify-center gap-6 mt-2 z-10 md:text-center">
          <p className="text-sm font-semibold text-blue-700 bg-white/70 border border-blue-200 px-4 py-2 rounded-full w-fit md:mx-auto">
            Pour coaching santé & changement d’habitudes
          </p>
          <h1 className="text-4xl md:text-5xl font-medium leading-tight max-w-3xl">
            Des clients réguliers entre check-ins-sans messagerie constante.
          </h1>
          <p className="text-md max-w-2xl mx-auto text-slate-700">
            Une app client simple pour l’accountability + un dashboard qui vous
            fait gagner du temps. Plus d’adhérence, meilleurs résultats,
            meilleure rétention.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:justify-center">
            <Link
              href="/start"
              className="bg-blue-600 text-white py-3 px-8 rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 text-center"
            >
              Commencer gratuitement
            </Link>
            <Link
              href="/#faq"
              className="bg-white text-slate-900 py-3 px-8 rounded-full font-semibold border border-blue-200 hover:bg-blue-50 transition-all duration-200 text-center"
            >
              Voir la FAQ
            </Link>
          </div>
        </div>
      </header>

      <main id="main-content" className="bg-white">
        <section className="py-16 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto bg-white border border-slate-200 rounded-3xl p-8">
            <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-3">
              Le vrai sujet : la régularité sans messagerie constante
            </h2>
            <ul className="mt-4 space-y-3 text-slate-700">
              {[
                "Les clients oublient quoi faire entre les check-ins.",
                "Les updates se dispersent entre DMs, captures et notes.",
                "Vous finissez à relancer l’adhérence au lieu de coacher le changement.",
              ].map((p) => (
                <li key={p} className="flex gap-3">
                  <span className="text-slate-400 font-bold">•</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="py-20 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-8">
            {[
              {
                title: "Moins de relances, plus de coaching",
                desc: "Arrêtez de demander “tu as tracké ?”. Featcal met l’accountability dans l’app.",
              },
              {
                title: "Suivez ce qui drive les résultats",
                desc: "Poids, pas, sommeil, hydratation, repas, workouts… choisissez ce qui compte et revoyez vite.",
              },
              {
                title: "Des plans qui sont suivis",
                desc: "Routines, plans et ressources livrés dans l’app, avec des rappels qui réduisent les abandons.",
              },
            ].map((card) => (
              <div
                key={card.title}
                className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/30"
              >
                <h2 className="text-xl font-semibold text-slate-900 mb-3">
                  {card.title}
                </h2>
                <p className="text-slate-700 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="py-20 px-8 xl:px-0 pt-0">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-start">
            <div className="bg-white border border-slate-200 rounded-3xl p-8">
              <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-4">
                Idéal pour
              </h2>
              <ul className="space-y-3 text-slate-700">
                {[
                  "Coaching perte / prise de poids avec check-in hebdo.",
                  "Habitudes (pas, sommeil, hydratation, structure des repas).",
                  "Programmes santé métabolique où la constance est clé.",
                  "Groupes où la standardisation compte.",
                ].map((p) => (
                  <li key={p} className="flex gap-3">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/30">
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">
                Ce que Featcal remplace
              </h3>
              <ul className="space-y-3 text-slate-700">
                {[
                  "DMs constants et messages “tu as tracké ?”.",
                  "Captures et notes dispersées.",
                  "Tableurs pour check-ins et progression.",
                  "Relances pour l’adhérence entre les séances.",
                ].map((p) => (
                  <li key={p} className="flex gap-3">
                    <span className="text-slate-400 font-bold">•</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <WhoWeServeFeatureSection
          eyebrow="Check-ins quotidiens"
          title="Une accountability qui tourne toute seule"
          description="Les clients suivent dans une app simple : vous arrêtez de courir après les updates et vos check-ins restent centrés sur le changement de comportement."
          bullets={[
            "Moins de messages “tu as suivi ?”.",
            "Plus de régularité entre check-ins.",
            "Un système de coaching plus scalable.",
          ]}
          imageSrc="/images/my-day.webp"
          imageAlt="Aperçu des check-ins quotidiens dans l’app client"
          align="right"
          imageFit="contain"
        />

        <section className="bg-gradient-to-b from-white to-blue-50 py-20 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto bg-white border border-blue-100 rounded-3xl p-8 md:p-10 shadow-xl shadow-blue-100/30">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-semibold text-slate-900">
                  Prêt à coacher les résultats, pas les rappels ?
                </h2>
                <p className="text-slate-700 mt-2">
                  Commencez gratuitement et faites votre prochain check-in avec
                  moins de relances.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  href="/start"
                  className="bg-blue-600 text-white py-3 px-8 rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 text-center"
                >
                  Commencer gratuitement
                </Link>
                <Link
                  href="/#faq"
                  className="bg-white text-slate-900 py-3 px-8 rounded-full font-semibold border border-blue-200 hover:bg-blue-50 transition-all duration-200 text-center"
                >
                  Voir la FAQ
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
